<template>
  <div class="slider-element">
    <range-slider-fi
      class="fi-px-6"
      :min="minValue"
      :max="maxValue"
      :value="[lower, upper]"
      :disabled="disabled"
      :interval="interval"
      @drag-end="onDragEndSlider"
      @change="onChangeSlider"
    />
    <div class="slider-element__text-input">
      <text-input-fi
        :model-value="lowerAsString"
        :label="`[${group.unit}]`"
        :disabled="disabled"
        :min="minValue"
        :max="maxValue"
        data-test="range-value-lower"
        show-localized-values
        @change="onInputLower"
      />
      <div class="fi-px-12">-</div>
      <text-input-fi
        :model-value="upperAsString"
        :label="`[${group.unit}]`"
        :disabled="disabled"
        :min="minValue"
        :max="maxValue"
        data-test="range-value-upper"
        show-localized-values
        @change="onInputUpper"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { RangeSliderFi, TextInputFi } from 'atoms';
import { computed, ref } from 'vue';

defineOptions({ name: 'SliderElement' });

const props = withDefaults(
  defineProps<{
    element?: Record<string, any>;
    group?: Record<string, any>;
  }>(),
  {
    element: () => ({}),
    group: () => ({}),
  },
);

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const lower = ref(0);
const upper = ref(0);

const minValue = computed(() => Math.floor(Number(props.element.absoluteMinValue)));
const maxValue = computed(() => Math.ceil(Number(props.element.absoluteMaxValue)));
const disabled = computed(() => props.element.disabled || minValue.value + 1 >= maxValue.value);
const interval = computed(() => {
  const { decimalPlaces } = props.group;
  if (!decimalPlaces) {
    return 1;
  }
  return 10 ** -decimalPlaces;
});

const lowerAsString = computed(() => String(lower.value));
const upperAsString = computed(() => String(upper.value));

function onDragEndSlider(newValue: number | number[] | null) {
  if (!Array.isArray(newValue) || !newValue.length) {
    console.error('invalid newValue', newValue);
    return;
  }
  [lower.value, upper.value] = newValue;
  emit('change');
}

function onChangeSlider(newValue: number | number[]) {
  if (Array.isArray(newValue)) {
    [lower.value, upper.value] = newValue;
  }
}

function onInputLower(newValueInput: string) {
  const newValue = Number(newValueInput);

  if (
    Number.isNaN(newValue) ||
    !Number.isInteger(newValue) ||
    newValue < minValue.value ||
    newValue > maxValue.value ||
    newValue > upper.value
  ) {
    return;
  }

  lower.value = newValue;
  emit('change');
}

function onInputUpper(newValueInput: string) {
  const newValue = Number(newValueInput);

  if (
    newValueInput === '' ||
    Number.isNaN(newValue) ||
    !Number.isInteger(newValue) ||
    newValue > maxValue.value ||
    newValue < minValue.value ||
    newValue < lower.value
  ) {
    return;
  }

  upper.value = newValue;
  emit('change');
}

// created — select rounded values when fact-finder returns default outer ranges
lower.value =
  props.element.selectedMinValue === props.element.absoluteMinValue
    ? minValue.value
    : Number(props.element.selectedMinValue);
upper.value =
  props.element.selectedMaxValue === props.element.absoluteMaxValue
    ? maxValue.value
    : Number(props.element.selectedMaxValue);

// FactFinderSliderFacet reads these from the component ref in its onApply handler
defineExpose({
  lower,
  upper,
  element: computed(() => props.element),
});
</script>

<style scoped src="./slider-element" />
